import React, {useEffect, useMemo, useState} from 'react';
import {useQuery} from "@tanstack/react-query";
import CloneAttachCellRenderer from "./CloneAttachCellRenderer";
import CloneModal from "./CloneModal";
import ClonePlateFormat from "./ClonePlateFormat.jsx";
import aggridUtils from "../../../../utils/aggrid-utils.js";
import {Card} from "../../../../components/card/card.jsx";
import {AgGridReact} from "ag-grid-react";
import clonesService from "../../../../services/web/clones-service.js";
import {FaCheck, FaTimes} from "react-icons/fa";
const BASENAME = import.meta.env.BASE_URL;

const columns = (filters) => [
    {headerName: "Data/Hora", field: "date_notification", cellClass: "!flex !items-center",
        comparator: aggridUtils.agGridCompareDateTime, sort: "desc"},
    {
        headerName: "Total",
        field: "total",
        comparator: (a, b, nodeA, nodeB, isInverted) => {
            return aggridUtils.nullLastComparator(a, b, nodeA, nodeB, isInverted);
        },
        width: 100,
        hide: !filters.last_notification,
        cellClass: "!flex !items-center !justify-center"
    },
    {
        headerName: "Placa",
        field: "plate",
        comparator: (a, b, nodeA, nodeB, isInverted) => {
            const valorA = a?.formattedValue;
            const valorB = b?.formattedValue;
            return aggridUtils.nullLastComparator(valorA, valorB, nodeA, nodeB, isInverted);
        },
        cellRenderer: ClonePlateFormat,
        valueFormatter: ({value}) => value.formattedValue,
        cellClass: "!flex !items-center !justify-center"
    },
    {
        headerName: "Verificado",
        field: "verified",
        comparator: (a, b, nodeA, nodeB, isInverted) => {
            return aggridUtils.nullLastComparator(a, b, nodeA, nodeB, isInverted);
        },
        width: 120,
        cellRenderer: ({value}) => value ? <FaCheck className="text-green-500" /> : <FaTimes className="text-gray-400" />,
        cellClass: "!flex !items-center !justify-center"
    },
    {
        headerName: "Natureza(s)",
        field: "reasons",
        comparator: (a, b, nodeA, nodeB, isInverted) => {
            return aggridUtils.nullLastComparator(a, b, nodeA, nodeB, isInverted);
        },
        flex: 1,
        cellRenderer: ({value}) => <span className="truncate" title={value}>{value}</span>,
        cellClass: "!flex !items-center"
    },
    {headerName: "Município 1", field: "citynamea", width: 175, cellClass: "!flex !items-center"},
    {
        headerName: "Imagem 1",
        field: "attach1",
        headerClass: "header-product",
        width: 125,
        sortable: false,
        cellRenderer: CloneAttachCellRenderer,
        cellClass: "!flex !items-center !justify-center"
    },
    {
        headerName: "Imagem 2",
        field: "attach2",
        headerClass: "header-product",
        width: 125,
        sortable: false,
        cellRenderer: CloneAttachCellRenderer,
        cellClass: "!flex !items-center !justify-center"
    },
    {headerName: "Município 2", field: "citynameb", width: 175, cellClass: "!flex !items-center"}
];

const CloneTable = ({filters, search, tableRef, setIsLoading}) => {
    const [columnDefs, setColumnDefs] = useState(columns(filters));
    const [show, setShow] = useState(false);
    const [rowSelected, setRowSelected] = useState(undefined);
    const theme = useMemo(() => {
        return aggridUtils.generalTheme();
    }, []);

    const {data, isLoading, refetch} = useQuery({
        queryFn: () => {
            const prepareFilters = Object.fromEntries(
                Object.entries(filters).map(([key, value]) => {
                    if (key === "plate" && value) value = value.replaceAll(" ", "");

                    if (typeof value === 'object' && value !== null && 'value' in value) {
                        return [key, value?.value ?? ""];
                    }

                    return [key, value];
                })
            );

            return clonesService.list({filters: prepareFilters}).then(({data}) => data)
        },
        queryKey: ['table', {search}]
    });

    useEffect(() => { if (search) setColumnDefs(columns(filters)) }, [search]);

    useEffect(() => {
        setIsLoading(isLoading)
    }, [isLoading]);

    const onCellClicked = (cell) => {
        if (cell.colDef.field === "total") {

            window.open(`${BASENAME}clones?plate=${cell.data.plate.rawValue}`, '_blank');
            return;
        }

        setShow(true);
        setRowSelected(cell.data);
    }

    const autoSizeStrategy = useMemo(
        () => ({
            type: "fitGridWidth",
        }), []
    );

    return (
        <>
            <Card>
                <Card.Body className="flex">
                    <div className="h-[calc(100vh-201px)] w-full">
                        <AgGridReact
                            ref={tableRef}
                            rowHeight={80}
                            rowData={data ?? []}
                            columnDefs={columnDefs}
                            autoSizeStrategy={autoSizeStrategy}
                            animateRows={false}
                            suppressCellFocus={true}
                            pagination
                            paginationPageSize={50}
                            paginationPageSizeSelector={[10, 50, 100, 500]}
                            onCellClicked={onCellClicked}
                            gridOptions={{localeText: aggridUtils.localization()}}
                            theme={theme}
                        />
                    </div>
                </Card.Body>
            </Card>

            <CloneModal
                show={show}
                setShow={setShow}
                rowSelected={rowSelected}
                setRowSelected={setRowSelected}
                tableRef={tableRef}
                filters={filters}
                refetch={refetch}
            />
        </>
    );
};

export default CloneTable;